import React from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Icon, Text, Dropdown } from '@deseretbook/react-ui'
import BreadcrumbButton from '../../buttons/BreadcrumbButton'
import formatAuthorNames from '../../../utils/formatAuthorNames'

import './TopToolbar.css'
import useDropdown from '../../../hooks/useDropdown'
import { detailsUrl } from '../../../utils/routes'
import CopyTooltip from '../../tooltips/CopyTooltip'

/**
 * Top Toolbar component for use in the Reader Page.
 *
 * @returns {function} Component
 */
function TopToolbar({
  ebook,
  chapter,
  className,
  onMenuClick,
  onPrevious,
  onNext,
  onToggleBookmarks,
  pageCountLabel,
  onToggleReaderOptions,
  onToggleNotesDisplay,
  onToggleContentSearch,
  onToggleOptionsDisplay,
  sku,
  mediaId,
  media,
  showToolbars,
  handleUpdateToolbars,
  readerOptions,
  ...rest
}) {
  const { dropdownProps, triggerProps } = useDropdown()
  const landingPage = detailsUrl(mediaId, sku)
  const { pageColor = {} } = readerOptions
  const { backgroundColor, color } = pageColor

  const authorNames = formatAuthorNames((media && media.authors) || [])

  const handleMouseEnter = () => {
    handleUpdateToolbars(true, true)
  }

  const handleMouseLeave = () => {
    handleUpdateToolbars(true, false)
  }

  const dbcomUrl = (ebook && ebook.productUrl) || ''

  return (
    <Box
      className="TopToolbarContainer"
      isFullwidth
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <Box
        className={`TopToolbar TopToolbar-${pageColor.id} ${showToolbars ? '' : 'hidden'}`}
        alignItems="center"
        justifyContent="space-between"
        isFullwidth
        style={{ backgroundColor, color }}
      >
        <Box
          alignItems="center"
          justifyContent="space-between"
          isFullwidth
          style={{ maxWidth: '272px' }}
        >
          <BreadcrumbButton colorStyle={pageColor.id} />
          <Button
            onClick={onMenuClick}
            className={`toolbar-button toolbar-button-${pageColor.id}`}
            style={{ margin: '0 32px' }}
          >
            <Icon
              name="chapters"
              className={`toolbar-button-icon toolbar-button-icon-${pageColor.id}`}
            />
          </Button>

          <Button
            onClick={onToggleReaderOptions}
            className={`toolbar-button toolbar-button-${pageColor.id}`}
          >
            <Icon
              name="fontStyle"
              className={`toolbar-button-icon toolbar-button-icon-${pageColor.id}`}
              style={{ height: '24px', width: '24px' }}
            />
          </Button>
        </Box>
        <Box isColumn alignItems="center" style={{ color: 'inherit' }}>
          <Text
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              color: 'inherit',
            }}
          >
            {ebook.package.metadata.title}
          </Text>
          <Text style={{ color: 'inherit' }}>{authorNames || 'Authors'}</Text>
        </Box>
        <Box
          alignItems="center"
          justifyContent="space-between"
          isFullwidth
          style={{ width: '272px' }}
        >
          <Button
            onClick={onToggleBookmarks}
            className={`toolbar-button toolbar-button-${pageColor.id}`}
          >
            <Icon
              name="bookmark"
              className={`toolbar-button-icon toolbar-button-icon-${pageColor.id}`}
              style={{ height: '30px', width: '30px', opacity: 0.65 }}
            />
          </Button>
          <Button
            onClick={onToggleNotesDisplay}
            className={`toolbar-button toolbar-button-${pageColor.id}`}
          >
            <Icon
              name="journal"
              className={`toolbar-button-icon toolbar-button-icon-${pageColor.id}`}
              style={{ height: '20px', width: '20px' }}
            />
          </Button>
          <Button
            onClick={onToggleContentSearch}
            className={`toolbar-button toolbar-button-${pageColor.id}`}
          >
            <Icon
              name="search"
              className={`toolbar-button-icon toolbar-button-icon-${pageColor.id}`}
              style={{ height: '24px', width: '24px' }}
            />
          </Button>
          <Box>
            <Button {...triggerProps} className={`toolbar-button toolbar-button-${pageColor.id}`}>
              <Icon
                name="options"
                className={`toolbar-button-icon toolbar-button-icon-${pageColor.id}`}
                style={{ height: '24px', width: '24px' }}
              />
            </Button>
            <Dropdown {...dropdownProps}>
              <Box isColumn alignItems="start" className="dropdown-link-container">
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={landingPage}
                  {...rest}
                  className="dropdown-link-item"
                >
                  <Icon name="info" className="link-icon" />
                  <Text>Title Details</Text>
                </a>
                <CopyTooltip textToCopy={dbcomUrl}>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(dbcomUrl)
                    }}
                    {...rest}
                    className="dropdown-link-item"
                    style={{ border: 'none' }}
                  >
                    <Icon name="share" className="link-icon" />
                    <Text>Share Title</Text>
                  </Button>
                </CopyTooltip>
              </Box>
            </Dropdown>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

TopToolbar.defaultProps = {
  ebook: {},
  chapter: {},
  className: '',
  onMenuClick: () => {},
  onNext: () => {},
  onPrevious: () => {},
  onToggleBookmarks: () => {},
  pageCountLabel: '',
}

TopToolbar.propTypes = {
  /** Current ebook object */
  ebook: PropTypes.shape({}),
  /** Current chapter object */
  chapter: PropTypes.shape({}),
  /** Additional class names. */
  className: PropTypes.string,
  /** Callback for menu button */
  onMenuClick: PropTypes.func,
  /** Callback for next button */
  onNext: PropTypes.func,
  /** Callback for previous button */
  onPrevious: PropTypes.func,
  /** Callback to toggle the display of bookmarks */
  onToggleBookmarks: PropTypes.func,
  /** The current page of total pages label */
  pageCountLabel: PropTypes.string,
}

export default TopToolbar
